/* eslint-disable react-hooks/exhaustive-deps */
import { LoadingOutlined } from '@ant-design/icons'
import { PageContent, Layout } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-tokens'
import { Spin } from 'antd/lib'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import analytics, { AnalyticsEvent, AnalyticsEventTrigger } from '../../util/analytics'
import { GateFeedFilter } from '../../clients/type'
import { AuthLayout } from '../../components/AuthLayout'
import { SiteDirectory } from '../../models/client/site'
import { User } from '../../models/client/user'
import { selectSiteDirectory } from '../../store/sites'
import { selectUser } from '../../store/user'
import { isEqual } from '../../util/isEqual'
import {
  selectChassisTypeMetadata,
  selectLoadTypeMetadata,
  selectVehicleTypeMetadata,
} from '../../store/metadata'

import { useReports } from '../../shared/hooks/useReports'
import type { Location, ViewableImage } from '../../models/client/reports'
import { ReportHeader } from '../../shared/components/ReportHeader'
import { ReportTable } from '../../shared/components/ReportTable'
import { DEFAULT_COLUMNS, TABLE_COLUMNS_OPTIONS } from './constants'
import { ImageGallery } from '../../components/ImageViewer'
import { useLocationGates } from '../../shared/hooks/useLocationGates'
import { AddAssetDrawer, ViewAssetDrawer } from '../../shared/components/AssetDrawer'

const StyledPageContent = styled(PageContent)`
  background-color: ${toCSSValue('color-background-secondary')};
  overflow: auto;
`

type GateFeedProps = {
  user: User
  locations: SiteDirectory
}

function GateFeedPage({ user, locations }: GateFeedProps): JSX.Element {
  const [selectedLocation, setSelectedLocation] = useState<Location>({
    label: 'All locations',
    value: 'all',
  })
  const [selectedCategory, setSelectedCategory] = useState({
    label: 'Carrier name',
    value: 'vehicle.carrier.name',
  })

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isViewAssetDrawerOpen, setIsViewAssetDrawerOpen] = useState(false)

  const [currentAsset, setCurrentAsset] = useState()
  const [gates, setGates] = useState([])
  const [selectedGate, setSelectedGate] = useState<{ label: string; value: string } | null>({
    label: 'All gates',
    value: 'all',
  })
  const [visibleImageData, setViewableImageData] = useState<null | ViewableImage>(null)

  const {
    assets,
    filters,
    addFilter,
    removeFilter,
    removePropertyFilters,
    clearAllFilters,
    isLoading,
    page: currentPage,
    setPage,
    totalItems,
    totalPages,
    sort,
    setSort,
    refresh,
  } = useReports({
    reportType: 'gatefeed',
    includeVehicles: true,
  })

  const gatesByLocation = useLocationGates(selectedLocation)

  const onRefreshPage = () => {
    setCurrentAsset(undefined)
    refresh()
  }

  const updateGateList = () => {
    setGates([{ label: 'All gates', value: 'all' }, ...gatesByLocation])
  }

  const handleOnAddAsset = () => {
    setIsDrawerOpen(true)
    setCurrentAsset(undefined)
    analytics.track(AnalyticsEvent.ReportsAddTransaction)
  }

  const handleEditAsset = () => {
    setIsDrawerOpen(true)
    setIsViewAssetDrawerOpen(false)
  }

  const handleOnAddAssetClose = () => {
    setIsDrawerOpen(false)
    setIsViewAssetDrawerOpen(false)
  }

  const handleOnEditAssetClose = () => {
    setIsDrawerOpen(false)
    setIsViewAssetDrawerOpen(true)
  }

  const handleViewAssetDrawerToggle = () => {
    setIsViewAssetDrawerOpen(!isViewAssetDrawerOpen)
  }

  const handleOpenAssetDrawer = (assetId) => {
    setIsViewAssetDrawerOpen(true)
    setCurrentAsset(assetId)
    analytics.track(AnalyticsEvent.ReportsClickedRow, AnalyticsEventTrigger.click)
  }

  const handleLocationChange = (location) => {
    if (!location.target.value) return

    const { value: currentLocation } = location?.target

    if (currentLocation.value === 'all') {
      removeFilter({
        type: 'site',
        id: selectedLocation?.value,
        label: 'Site ID',
      })
      setSelectedLocation(currentLocation)
      return
    }

    setSelectedLocation(currentLocation)

    const locationFilter: GateFeedFilter = {
      type: 'site',
      id: currentLocation.id,
      label: 'Site ID',
      value: {
        label: currentLocation.name || '',
        value: currentLocation.id || '',
      },
    }
    addFilter(locationFilter)

    updateGateList()
  }

  const handleGateChange = (gate) => {
    if (!gate.target.value) return

    const { value: currentGate } = gate?.target

    analytics.track(AnalyticsEvent.ReportsChooseGate, AnalyticsEventTrigger.click, {
      gate: currentGate.label,
    })

    if (currentGate.value === 'all') {
      removeFilter({
        type: 'gate',
        id: selectedGate?.value,
        label: 'Gate ID',
      })
      setSelectedGate(currentGate)
      return
    }

    setSelectedGate(currentGate)

    const gateFilter: GateFeedFilter = {
      type: 'gate',
      id: currentGate.value,
      label: 'Gate ID',
      value: {
        label: gate.label || '',
        value: currentGate.value || '',
      },
    }
    addFilter(gateFilter)
  }

  const handleSearchCategoryChange = (category: {
    target: { value: { label: string; value: string } }
  }) => {
    const { value: currentCategory } = category?.target
    setSelectedCategory(currentCategory)
  }

  useEffect(() => {
    updateGateList()
  }, [gatesByLocation])

  useEffect(() => {
    analytics.track(AnalyticsEvent.PageView)
  }, [])

  return (
    <>
      <StyledPageContent>
        <ReportHeader
          title="Gate Feed"
          user={user}
          locations={locations}
          filters={filters}
          selectedLocation={selectedLocation}
          handleLocationChange={handleLocationChange}
          onAddAsset={handleOnAddAsset}
          isLoading={isLoading}
          isGateFeed
        />
        <PageContent.Body>
          <Layout.Stack gap="spacing-5">
            <ReportTable
              isLoading={isLoading}
              selectedLocation={selectedLocation}
              selectedCategory={selectedCategory}
              selectedGate={selectedGate}
              filters={filters}
              data={assets}
              gates={gates}
              page={currentPage}
              totalItems={totalItems}
              totalPages={totalPages}
              onAddFilter={addFilter}
              onOpenAsset={handleOpenAssetDrawer}
              onViewImage={setViewableImageData}
              onRemoveFilter={removeFilter}
              onRemovePropertyFilters={removePropertyFilters}
              handlePageChange={setPage}
              handleSearchCategoryChange={handleSearchCategoryChange}
              handleGateChange={handleGateChange}
              onResetFilters={clearAllFilters}
              columns={DEFAULT_COLUMNS}
              columnOptions={TABLE_COLUMNS_OPTIONS}
              sort={sort}
              setSort={setSort}
              enhancedFilters
            />
          </Layout.Stack>
        </PageContent.Body>
      </StyledPageContent>
      <AddAssetDrawer
        open={isDrawerOpen}
        handleAddAssetClose={handleOnAddAssetClose}
        handleEditAssetClose={handleOnEditAssetClose}
        user={user}
        locations={locations}
        selectedLocation={selectedLocation}
        asset={currentAsset}
        refresh={refresh}
        isGateFeedAsset
      />
      <ViewAssetDrawer
        open={isViewAssetDrawerOpen}
        handleClose={handleViewAssetDrawerToggle}
        onViewImage={setViewableImageData}
        onEdit={handleEditAsset}
        asset={currentAsset}
        refresh={onRefreshPage}
      />
      <ImageGallery
        visibleData={
          visibleImageData
            ? {
                images: visibleImageData.images,
                path: visibleImageData.path,
                imagePaths: visibleImageData.imagePaths,
                id: visibleImageData.observationId,
                timezone: visibleImageData.timezone,
              }
            : null
        }
        onClose={() => setViewableImageData(null)}
      />
    </>
  )
}

export function GateFeed() {
  const user = useSelector(selectUser, isEqual)
  const locations = useSelector(selectSiteDirectory, isEqual)
  const vehicleTypes = useSelector(selectVehicleTypeMetadata, isEqual)
  const loadTypes = useSelector(selectLoadTypeMetadata, isEqual)
  const chassisTypes = useSelector(selectChassisTypeMetadata, isEqual)

  const isLoading = !user || !locations || !vehicleTypes || !loadTypes || !chassisTypes

  return (
    <AuthLayout>
      {isLoading ? (
        <StyledPageContent style={{ alignContent: 'center', textAlign: 'center' }}>
          <PageContent.Body>
            <Layout.Stack>
              <Spin indicator={<LoadingOutlined spin />} size="large" />
              <h3>Loading...</h3>
            </Layout.Stack>
          </PageContent.Body>
        </StyledPageContent>
      ) : (
        <GateFeedPage user={user} locations={locations} />
      )}
    </AuthLayout>
  )
}
